import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';
import { clearAuth } from '../lib/auth-store.ts';
import { request } from '../lib/api.ts';
import { fetchAccounts, fetchCampaigns, fetchDrafts } from '../lib/queries.ts';
import { useAuthUser } from '../lib/use-auth.ts';
import { SUPPORTED_LANGUAGES } from '../i18n/index.ts';

export function DashboardLayout(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const user = useAuthUser();
  const location = useLocation();

  const draftsQuery = useQuery({
    queryKey: ['drafts', 'count', 'pending'],
    queryFn: () => fetchDrafts({ moderationStatus: 'pending', limit: 1 }),
    refetchInterval: 60_000,
  });
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => fetchAccounts() });
  const campaignsQuery = useQuery({ queryKey: ['campaigns'], queryFn: fetchCampaigns });

  const pendingCount = draftsQuery.data?.pagination.total ?? 0;
  const accountsCount = accountsQuery.data?.accounts.length ?? 0;
  const campaignsCount = campaignsQuery.data?.campaigns.length ?? 0;

  async function handleLogout(): Promise<void> {
    try {
      await request('/auth/logout', { method: 'POST' });
    } catch {
      /* ignore */
    }
    clearAuth();
  }

  function changeLanguage(lng: string): void {
    void i18n.changeLanguage(lng);
  }

  return (
    <div className="dashboard-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="logo">CE</div>
          <div className="name">
            ContentEngine<span className="small">v0.1 · dev</span>
          </div>
        </div>
        <nav className="nav">
          <div className="group-title">{t('nav.operations')}</div>
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="icon">⚡</span> {t('nav.overview')}
          </NavLink>
          <NavLink to="/campaigns" className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="icon">🎯</span> {t('nav.campaigns')}
            {campaignsCount > 0 && <span className="badge">{campaignsCount}</span>}
          </NavLink>
          <NavLink to="/queue" className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="icon">⏳</span> {t('nav.queue')}
            {pendingCount > 0 && <span className="badge amber">{pendingCount}</span>}
          </NavLink>
          <NavLink to="/posts" className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="icon">📤</span> {t('nav.posts')}
          </NavLink>
          <NavLink to="/films" className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="icon">🎬</span> {t('nav.films')}
          </NavLink>
          <NavLink to="/clips" className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="icon">🎞️</span> {t('nav.clips')}
          </NavLink>
          <NavLink to="/variants" className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="icon">🎞</span> {t('nav.variants')}
          </NavLink>
          <NavLink to="/banners" className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="icon">🖼️</span> {t('nav.banners')}
          </NavLink>

          <div className="group-title">{t('nav.infrastructure')}</div>
          <NavLink to="/accounts" className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="icon">📱</span> {t('nav.accounts')}
            {accountsCount > 0 && <span className="badge">{accountsCount}</span>}
          </NavLink>
          <a style={{ opacity: 0.4, cursor: 'not-allowed' }} title={t('common.phase2')}>
            <span className="icon">🌐</span> {t('nav.proxies')}
          </a>
          <a style={{ opacity: 0.4, cursor: 'not-allowed' }} title={t('common.phase2')}>
            <span className="icon">🤖</span> {t('nav.pipelines')}
          </a>

          <div className="group-title">{t('nav.insights')}</div>
          <a style={{ opacity: 0.4, cursor: 'not-allowed' }} title={t('common.phase2')}>
            <span className="icon">📈</span> {t('nav.analytics')}
          </a>
          <a style={{ opacity: 0.4, cursor: 'not-allowed' }} title={t('common.phase2')}>
            <span className="icon">🔍</span> {t('nav.trends')}
          </a>

          <div className="group-title">{t('nav.setup')}</div>
          {user?.role === 'admin' && (
            <NavLink to="/users" className={({ isActive }) => (isActive ? 'active' : '')}>
              <span className="icon">👤</span> {t('nav.users')}
            </NavLink>
          )}
          {user?.role === 'admin' && (
            <NavLink to="/audit" className={({ isActive }) => (isActive ? 'active' : '')}>
              <span className="icon">📋</span> {t('nav.audit')}
            </NavLink>
          )}
          {user?.role === 'admin' && (
            <NavLink to="/jobs" className={({ isActive }) => (isActive ? 'active' : '')}>
              <span className="icon">⏳</span> {t('nav.jobs')}
            </NavLink>
          )}
          <a style={{ opacity: 0.4, cursor: 'not-allowed' }} title={t('common.phase2')}>
            <span className="icon">⚙</span> {t('nav.prompts')}
          </a>
        </nav>
        <div className="system-status">
          <div className="indicator">{t('system.operational')}</div>
          <div className="info">
            {user ? `${user.email} · ${user.role}` : t('system.noSession')}
            <br />
            <button
              onClick={() => void handleLogout()}
              style={{
                marginTop: 6,
                background: 'transparent',
                border: '1px solid var(--line-strong)',
                color: 'var(--text-2)',
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: 11,
                cursor: 'pointer',
                fontFamily: 'var(--mono)',
              }}
            >
              {t('auth.login.logout')}
            </button>
          </div>
        </div>
      </aside>
      <main className="main">
        <div className="topbar">
          <div className="crumbs">
            content-engine <span className="sep">/</span>{' '}
            <span className="here">
              {location.pathname.split('/').filter(Boolean)[0] ?? 'overview'}
            </span>
          </div>
          <div className="actions">
            <span className="chip queue">↻ {t('topbar.live')}</span>
            <div
              style={{
                display: 'flex',
                gap: 0,
                border: '1px solid var(--line)',
                borderRadius: 6,
                overflow: 'hidden',
                fontFamily: 'var(--mono)',
                fontSize: 11,
              }}
              title={t('language.switch')}
            >
              {SUPPORTED_LANGUAGES.map((lng) => (
                <button
                  key={lng}
                  onClick={() => changeLanguage(lng)}
                  style={{
                    background: i18n.resolvedLanguage === lng ? 'var(--neon-soft)' : 'transparent',
                    color: i18n.resolvedLanguage === lng ? 'var(--neon)' : 'var(--text-2)',
                    border: 'none',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                  }}
                >
                  {lng}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
